import React from "react";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

interface SnackbarContextValue {
  snackBars: any[];
  showSnackbar: (
    message?: string,
    status?: string,
    autoHideDuration?: number,
  ) => number;
  handleClose: (id: any) => void;
}

const SnackbarContext = React.createContext<SnackbarContextValue>({
  snackBars: [],
  showSnackbar: () => 0,
  handleClose: () => {},
});

const Alert = React.forwardRef(function Alert(props: any, ref: any) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function SnackbarComponent() {
  const { snackBars, handleClose } = React.useContext(SnackbarContext);

  const _handleClose = (e, id, reason?: string) => {
    // Ignore click-away so a snackbar shown as a side effect of the same
    // click (e.g. a blur handler) isn't dismissed the instant it mounts.
    if (reason === "clickaway") return;
    e?.stopPropagation();
    handleClose(id);
  };

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      {snackBars.map((snackbar, index) => (
        <Snackbar
          key={snackbar.id} // Use unique ID to avoid duplicates
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={snackbar.open}
          autoHideDuration={snackbar.autoHideDuration ?? 4000}
          onClose={(e) => _handleClose(e, snackbar.id)}
          style={{ bottom: `${20 + index * 60}px` }} // Dynamic position
        >
          <Alert
            onClose={(e) => _handleClose(e, snackbar.id)}
            severity={snackbar.severity}
            sx={{
              width: "100%",
              backgroundColor:
                snackbar.severity === "info" ? "#0288d1" : undefined, // Blue color for info
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      ))}
    </Stack>
  );
}

function SnackbarProvider({ children }: { children: React.ReactNode }) {
  const [snackBars, setSnackBars] = React.useState([]);

  const showSnackbar = (
    message = "Success",
    status = "success",
    autoHideDuration = 4000,
  ) => {
    const id = new Date().getTime(); // Generate a unique ID
    setSnackBars((prev) => {
      // Check if the same message already exists
      if (prev.some((snackbar) => snackbar.message === message)) {
        return prev;
      }
      return [
        ...prev,
        { id, message, severity: status, open: true, autoHideDuration },
      ];
    });

    return id;
  };

  const handleClose = (id: any) => {
    setSnackBars((prev) =>
      prev.map((snackbar) =>
        snackbar.id === id ? { ...snackbar, open: false } : snackbar,
      ),
    );

    setTimeout(() => {
      setSnackBars((prev) => prev.filter((snackbar) => snackbar.id !== id));
    }, 400);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar, snackBars, handleClose }}>
      <SnackbarComponent />
      {children}
    </SnackbarContext.Provider>
  );
}

const useSnackbar = () => {
  const { showSnackbar, handleClose } = React.useContext(SnackbarContext);

  return { showSnackbar, handleClose };
};

export { SnackbarProvider, useSnackbar };
